import { Select } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from "jotai";
import { projectMembersWithRolesAtom, projectMembersWithRolesAsyncAtom } from "~/components/Map/Member/MemberStore";
import { currentProjectAtom } from "~/stores/ProjectStore";

export interface GeocollectMembersSelectorProps {
  defaultValue?: number | number[];
  onChange: Function;
  disabled?: boolean;
  forcedReload?: number;
  multiple?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
  placeholder?: string;
  value?: number | number[];
  style?: React.CSSProperties;
}

interface OptionType {
  label: string;
  value: number;
}

/**
 * select option for members
 * @returns
 */
const GeocollectMembersSelector = (props: GeocollectMembersSelectorProps) => {
  // Get members from Jotai store and fetch function
  const memberWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const currentProject = useAtomValue(currentProjectAtom);
  const fetchMembers = useSetAtom(projectMembersWithRolesAsyncAtom);

  // display value
  const [options, setOptions] = useState<OptionType[]>([]);

  // Auto-fetch members when component mounts and members list is empty
  useEffect(() => {
    if (memberWithRoles.length === 0) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update options when members or props change
  useEffect(() => {
    const memberMap = new Map<number, string>();
    if (currentProject?.owner?.id) {
      memberMap.set(
        currentProject.owner.id,
        currentProject.owner.fullname || currentProject.owner.username || `User ${currentProject.owner.id}`
      );
    }
    memberWithRoles.forEach((member: any) => {
      if (member.user?.id) {
        memberMap.set(
          member.user.id,
          member.user.fullname || member.user.username || `User ${member.user.id}`
        );
      }
    });

    const members = Array.from(memberMap.entries())
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Prepare options with or without "All" option
    const allOptions = [
      ...(props.showAllOption ? [{
        label: props.allOptionLabel || "Tất cả",
        value: -1, // Using -1 to represent "All" option
      }] : []),
      ...members
    ];

    setOptions(allOptions);
  }, [memberWithRoles, currentProject?.owner, props.showAllOption, props.allOptionLabel]);

  const onChange = (value: number | number[]) => {
    if (props.onChange) {
      if (value) {
        props.onChange(value);
      } else {
        props.onChange(props.multiple ? [] : null);
      }
    }
  };

  const onSearch = (value: string) => {
    // console.log('search:', value);
  };

  // Filter `option.label` match the user type `input`
  const filterOption = (input: string, option?: { label: string; value: number }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  // Get the value to display
  let displayValue = props.value !== undefined ? props.value : props.defaultValue;
  if (props.showAllOption && displayValue === -1) {
    displayValue = -1;
  }

  // offline filter
  return (
    <Select
      showSearch
      placeholder={props.placeholder || "Chọn thành viên..."}
      optionFilterProp="children"
      onChange={onChange}
      onSearch={onSearch}
      filterOption={filterOption}
      options={options}
      value={displayValue}
      mode={props.multiple ? "multiple" : undefined}
      style={props.style || { width: '100%' }}
      disabled={props.disabled}
      maxTagCount={props.multiple ? "responsive" : undefined}
    />
  );
};

export default memo(GeocollectMembersSelector);